import { useState, useRef, useCallback, useEffect } from "react";
import { ArrowLeft } from "lucide-react";

type BodyView = "front" | "back" | "left" | "right";
type HeadView = "head-front" | "head-left" | "head-right";

export interface BodyMapValue {
  x: number;
  y: number;
  view: BodyView | HeadView;
  radius: number;
  gender?: "male" | "female";
  sizeInches?: string;
  placementNotes?: string;
}

interface Props {
  value: BodyMapValue | null;
  onChange: (v: BodyMapValue) => void;
  onNext: () => void;
  onPhaseChange?: (phase: "zone" | "sub" | "size" | "done") => void;
  /** When true, always open on the pin-placement screen even if value exists */
  startAtPin?: boolean;
  /** Override for the main heading on the pin screen */
  heading?: string;
  /** Override for the size question label in the refine screen */
  sizeQuestion?: string;
  /** Override for the pin overlay prompt copy */
  pinPromptOverlay?: string;
  /** Override for the toast copy when no pin is placed yet */
  toastNoPinCopy?: string;
  /** Service type — drives piercing-specific view and zone logic. Tattoo flow is byte-for-byte identical when absent. */
  serviceType?: string;
}

const VW = 90;
const VH = 160;
const MIN_R          = 2;
const MAX_R          = 13;
const DEFAULT_R      = 7;
const TOUCH_OFFSET_Y = -45;
const LOUPE_R        = 24;
const LOUPE_LIFT     = 60;
const LOUPE_ZOOM     = 1.7;

const VIEW_OPTIONS: { id: BodyView; label: string }[] = [
  { id: "front", label: "Front" },
  { id: "back",  label: "Back"  },
  { id: "left",  label: "Left"  },
  { id: "right", label: "Right" },
];

/* ── Piercing-only view groups ────────────────────────────────────── */
const PIERCING_HEAD_VIEW_OPTIONS: { id: HeadView; label: string }[] = [
  { id: "head-front", label: "Face" },
  { id: "head-left",  label: "Left" },
  { id: "head-right", label: "Right" },
];
const PIERCING_BODY_VIEW_OPTIONS: { id: BodyView; label: string }[] = [
  { id: "front", label: "Front" },
  { id: "back",  label: "Back"  },
  { id: "left",  label: "Left"  },
  { id: "right", label: "Right" },
];

const BODY_IMAGE: Record<"male" | "female", Record<BodyView, string>> = {
  male: {
    front: "/images/body-map/male-front.webp",
    back:  "/images/body-map/male-back.webp",
    left:  "/images/body-map/male-left.webp",
    right: "/images/body-map/male-right.webp",
  },
  female: {
    front: "/images/body-map/female-front.webp",
    back:  "/images/body-map/female-back.webp",
    left:  "/images/body-map/female-left.webp",
    right: "/images/body-map/female-right.webp",
  },
};

const OTHER_ZONE_ID    = "other";
const OTHER_SUB_ID     = "other-custom";
const OTHER_ZONE_LABEL = "Somewhere Else";

interface SubLocation { id: string; label: string }
interface Zone { id: string; label: string; icon: string; subs: SubLocation[] }

const ZONES: Zone[] = [
  {
    id: "head-neck", label: "Head & Neck", icon: "🗣️",
    subs: [
      { id: "scalp",    label: "Head / Scalp"       },
      { id: "face",     label: "Face"                },
      { id: "neck-f",   label: "Neck (front)"        },
      { id: "nape",     label: "Nape / Back of Neck" },
      { id: "ear",      label: "Behind the Ear"      },
    ],
  },
  {
    id: "chest-front", label: "Chest & Front", icon: "🫀",
    subs: [
      { id: "collarbone",  label: "Collarbone / Clavicle"    },
      { id: "sternum",     label: "Chest (center / sternum)" },
      { id: "chest-side",  label: "Chest (side)"             },
      { id: "stomach",     label: "Stomach / Abdomen"        },
      { id: "lower-torso", label: "Lower Torso"              },
    ],
  },
  {
    id: "back-shoulders", label: "Back & Shoulders", icon: "💆",
    subs: [
      { id: "upper-back",     label: "Upper Back"       },
      { id: "mid-back",       label: "Mid Back / Spine"  },
      { id: "lower-back",     label: "Lower Back"        },
      { id: "shoulder-top",   label: "Shoulder (top)"    },
      { id: "shoulder-blade", label: "Shoulder Blade"    },
    ],
  },
  {
    id: "arms", label: "Arms", icon: "💪",
    subs: [
      { id: "upper-arm-out", label: "Upper Arm (outer)"  },
      { id: "inner-bicep",   label: "Inner Arm / Bicep"  },
      { id: "elbow",         label: "Elbow / Ditch"      },
      { id: "forearm-out",   label: "Forearm (outer)"    },
      { id: "forearm-in",    label: "Forearm (inner)"    },
      { id: "wrist",         label: "Wrist"              },
    ],
  },
  {
    id: "hands", label: "Hands & Fingers", icon: "🤚",
    subs: [
      { id: "hand-back", label: "Hand (back)" },
      { id: "palm",      label: "Palm"         },
      { id: "fingers",   label: "Fingers"      },
      { id: "knuckles",  label: "Knuckles"     },
    ],
  },
  {
    id: "ribs-hip", label: "Ribs, Hip & Glutes", icon: "🧍",
    subs: [
      { id: "ribs",      label: "Ribs"             },
      { id: "hip",       label: "Hip"               },
      { id: "glutes",    label: "Glutes / Butt"     },
      { id: "inner-hip", label: "Groin / Inner Hip" },
    ],
  },
  {
    id: "legs", label: "Legs", icon: "🦵",
    subs: [
      { id: "thigh-out",    label: "Thigh (outer)"        },
      { id: "thigh-in",     label: "Thigh (inner)"        },
      { id: "knee",         label: "Knee / Kneecap"       },
      { id: "back-of-knee", label: "Back of Knee / Ditch" },
      { id: "calf",         label: "Calf"                 },
      { id: "shin",         label: "Shin"                 },
    ],
  },
  {
    id: "feet-ankles", label: "Feet & Ankles", icon: "🦶",
    subs: [
      { id: "ankle",    label: "Ankle"       },
      { id: "top-foot", label: "Top of Foot" },
      { id: "sole",     label: "Sole / Arch" },
      { id: "toes",     label: "Toes"        },
    ],
  },
  {
    id: OTHER_ZONE_ID, label: OTHER_ZONE_LABEL, icon: "✏️",
    // Placeholder sub — flow uses it so zone → sub → size → done logic still works
    subs: [{ id: OTHER_SUB_ID, label: "Custom placement" }],
  },
];

const SIZE_OPTIONS: { id: string; label: string; sub: string }[] = [
  { id: "tiny",   label: "Tiny",    sub: "1 in or less" },
  { id: "small",  label: "Small",   sub: "2 – 3 in"     },
  { id: "medium", label: "Medium",  sub: "4 – 6 in"     },
  { id: "large",  label: "Large",   sub: "7 – 10 in"    },
  { id: "xlarge", label: "X-Large", sub: "11 in +"      },
];

/* ── Piercing-only constants (never affect tattoo flow) ──────────── */

const PIERCING_HEAD_ZONES: Zone[] = [
  {
    id: "ear", label: "Ear", icon: "👂",
    subs: [
      { id: "lobe",           label: "Lobe"            },
      { id: "helix",          label: "Helix"           },
      { id: "forward-helix",  label: "Forward Helix"   },
      { id: "tragus",         label: "Tragus"          },
      { id: "anti-tragus",    label: "Anti-tragus"     },
      { id: "daith",          label: "Daith"           },
      { id: "rook",           label: "Rook"            },
      { id: "conch",          label: "Conch"           },
      { id: "industrial",     label: "Industrial"      },
      { id: "orbital",        label: "Orbital"         },
      { id: "anti-helix",     label: "Anti-helix / Snug" },
    ],
  },
  {
    id: "nose", label: "Nose", icon: "👃",
    subs: [
      { id: "nostril", label: "Nostril" },
      { id: "septum",  label: "Septum"  },
      { id: "bridge",  label: "Bridge"  },
    ],
  },
  {
    id: "face", label: "Face", icon: "😊",
    subs: [
      { id: "eyebrow",      label: "Eyebrow"         },
      { id: "cheek-dimple", label: "Cheek / Dimple"  },
      { id: "dermal-face",  label: "Dermal"          },
    ],
  },
  {
    id: "oral", label: "Oral", icon: "👄",
    subs: [
      { id: "lip-labret",  label: "Lip / Labret"         },
      { id: "medusa",      label: "Medusa / Philtrum"    },
      { id: "tongue",      label: "Tongue"               },
      { id: "tongue-web",  label: "Tongue Web"           },
    ],
  },
  {
    id: OTHER_ZONE_ID, label: OTHER_ZONE_LABEL, icon: "✏️",
    subs: [{ id: OTHER_SUB_ID, label: "Custom placement" }],
  },
];

const PIERCING_BODY_ZONES: Zone[] = [
  {
    id: "navel", label: "Navel", icon: "🫃",
    subs: [
      { id: "navel-standard", label: "Standard" },
      { id: "navel-inverse",  label: "Inverse"  },
    ],
  },
  {
    id: "nipple", label: "Nipple", icon: "⬤",
    subs: [
      { id: "nipple-left",  label: "Left"  },
      { id: "nipple-right", label: "Right" },
      { id: "nipple-both",  label: "Both"  },
    ],
  },
  {
    id: "surface-dermal", label: "Surface / Dermal", icon: "💎",
    subs: [
      { id: "surface-body",  label: "Surface"          },
      { id: "dermal-body",   label: "Dermal / Anchor"  },
    ],
  },
  {
    id: "collarbone-sternum", label: "Collarbone / Sternum", icon: "🦴",
    subs: [
      { id: "collarbone", label: "Collarbone" },
      { id: "sternum",    label: "Sternum"    },
    ],
  },
  {
    id: OTHER_ZONE_ID, label: OTHER_ZONE_LABEL, icon: "✏️",
    subs: [{ id: OTHER_SUB_ID, label: "Custom placement" }],
  },
];

const PIERCING_TYPES: { id: string; label: string; sub: string }[] = [
  { id: "ring-cbr",       label: "Ring / CBR",         sub: "Captive bead ring"       },
  { id: "curved-barbell", label: "Curved Barbell",      sub: "Banana bar / CBB"        },
  { id: "straight-barbell",label: "Straight Barbell",   sub: "Industrial, tongue etc." },
  { id: "labret-stud",    label: "Labret Stud",         sub: "Long post, flat back"    },
  { id: "flat-back",      label: "Flat-back Stud",      sub: "Threadless / push-in"   },
  { id: "retainer",       label: "Retainer",            sub: "Bioplast / clear"        },
  { id: "artist-choice",  label: "Artist's choice",     sub: ""                        },
];

/* ── Inline SVG head silhouette components ─────────────────────────
   These are only ever rendered when serviceType === "piercing" and the
   active view is a head view. Tattoo flow never touches this code.      */

function HeadFrontSVG({ gender }: { gender: "male" | "female" }) {
  const jaw = gender === "female" ? "M 22,62 Q 35,82 45,84 Q 55,82 68,62" : "M 20,60 Q 33,80 45,82 Q 57,80 70,60";
  return (
    <g opacity={0.82}>
      {/* neck */}
      <rect x={38} y={82} width={14} height={14} rx={3} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.5} />
      {/* head oval */}
      <ellipse cx={45} cy={42} rx={25} ry={30} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.5} />
      {/* jaw curve */}
      <path d={jaw} fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth={1.2} />
      {/* left ear */}
      <path d="M 20,38 Q 14,38 13,44 Q 13,50 20,52" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.5} />
      {/* left ear detail: lobe bump */}
      <path d="M 20,52 Q 16,56 20,58" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth={1} />
      {/* right ear */}
      <path d="M 70,38 Q 76,38 77,44 Q 77,50 70,52" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.5} />
      {/* right ear detail: lobe bump */}
      <path d="M 70,52 Q 74,56 70,58" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth={1} />
      {/* eyes */}
      <ellipse cx={36} cy={40} rx={4} ry={2.5} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth={1} />
      <ellipse cx={54} cy={40} rx={4} ry={2.5} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth={1} />
      {/* nose */}
      <path d="M 45,44 L 43,54 Q 45,56 47,54 L 45,44" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={0.9} />
      {/* mouth */}
      <path d="M 39,62 Q 45,67 51,62" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth={1} />
      {/* brow hints */}
      <path d="M 32,36 Q 36,34 40,36" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={0.8} />
      <path d="M 50,36 Q 54,34 58,36" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={0.8} />
    </g>
  );
}

function HeadSideSVG({ side, gender }: { side: "left" | "right"; gender: "male" | "female" }) {
  /* The side views are mirrored so the ear always faces the viewer.
     "head-left" = viewer sees left side of client (ear on right of SVG).
     "head-right" = viewer sees right side of client (ear on right of SVG as well due to flip).
     We draw a canonical "right-facing" profile (ear on right side of SVG, cx~55)
     and flip the whole group for head-left via a horizontal mirror transform. */
  const flip = side === "left"
    ? `scale(-1,1) translate(-${VW},0)`
    : undefined;

  const jawPath = gender === "female"
    ? "M 28,40 Q 26,60 30,72 Q 38,84 48,82 Q 54,80 55,75"
    : "M 26,38 Q 24,58 28,72 Q 36,84 47,82 Q 54,80 56,75";

  return (
    <g transform={flip} opacity={0.82}>
      {/* neck stump */}
      <rect x={34} y={80} width={16} height={14} rx={3} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth={1.4} />
      {/* head profile */}
      <path
        d="M 28,38 Q 28,12 45,10 Q 62,8 65,28 Q 68,40 65,55 Q 63,70 55,75"
        fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.5}
      />
      {/* jaw */}
      <path d={jawPath} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth={1.4} />
      {/* ── EAR (right side of SVG — always faces viewer) ─────────── */}
      {/* outer helix rim */}
      <path
        d="M 62,32 Q 74,32 76,44 Q 76,54 74,60 Q 71,68 64,68"
        fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.6}
      />
      {/* anti-helix */}
      <path
        d="M 64,36 Q 71,36 72,44 Q 72,52 70,56 Q 68,61 65,63"
        fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth={1}
      />
      {/* tragus */}
      <path
        d="M 63,46 Q 62,42 64,40"
        fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth={1.1}
      />
      {/* anti-tragus */}
      <path
        d="M 64,62 Q 62,64 63,67"
        fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth={1}
      />
      {/* lobe */}
      <path
        d="M 64,68 Q 63,74 65,76 Q 67,78 68,74 Q 68,70 65,68"
        fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.1}
      />
      {/* conch bowl hint */}
      <path
        d="M 65,44 Q 68,46 68,52 Q 68,58 65,60"
        fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={0.8}
      />
      {/* ── FACE FEATURES ─────────────────────────────────────────── */}
      {/* eye socket */}
      <ellipse cx={38} cy={33} rx={5} ry={4} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={0.9} />
      {/* brow */}
      <path d="M 33,28 Q 38,26 44,29" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={0.8} />
      {/* nose bridge + tip */}
      <path d="M 43,30 L 44,48 Q 46,52 47,50" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={0.8} />
      {/* nostril */}
      <path d="M 43,50 Q 41,53 43,55" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={0.8} />
      {/* mouth/lips */}
      <path d="M 35,62 Q 38,58 41,60 Q 43,62 41,66 Q 38,68 35,66" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={0.9} />
    </g>
  );
}

function HeadSVG({ view, gender }: { view: HeadView; gender: "male" | "female" }) {
  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      className="absolute inset-0 w-full h-full pointer-events-none select-none"
      style={{ opacity: 1 }}
    >
      {view === "head-front" && <HeadFrontSVG gender={gender} />}
      {view === "head-left"  && <HeadSideSVG side="left"  gender={gender} />}
      {view === "head-right" && <HeadSideSVG side="right" gender={gender} />}
    </svg>
  );
}

/* ── Helper: is the given view a head view? ──────────────────────── */
function isHeadView(v: BodyMapValue["view"]): v is HeadView {
  return v === "head-front" || v === "head-left" || v === "head-right";
}

function isBodyView(v: BodyMapValue["view"]): v is BodyView {
  return v === "front" || v === "back" || v === "left" || v === "right";
}

/* ── deriveIdsFromValue: parses placementNotes/sizeInches back to IDs ── */
function deriveIdsFromValue(
  pn: string | null | undefined,
  si: string | null | undefined,
  zones: Zone[],
  sizeOptions: typeof SIZE_OPTIONS | typeof PIERCING_TYPES,
) {
  let zoneId: string | null = null;
  let subId:  string | null = null;
  let sizeId: string | null = null;
  let otherDesc = "";
  if (pn) {
    const sep = pn.indexOf(" — ");
    if (sep !== -1) {
      const zLabel = pn.slice(0, sep);
      const sLabel = pn.slice(sep + 3);
      const z = zones.find(z2 => z2.label === zLabel);
      if (z) {
        zoneId = z.id;
        if (z.id === OTHER_ZONE_ID) {
          subId = OTHER_SUB_ID;
          otherDesc = sLabel;
        } else {
          const s = z.subs.find(s2 => s2.label === sLabel);
          if (s) subId = s.id;
        }
      }
    }
  }
  if (si) {
    const match = sizeOptions.find(o => si.startsWith(o.label));
    if (match) sizeId = match.id;
  }
  return { zoneId, subId, sizeId, otherDesc };
}

function getTouchDist(touches: React.TouchList) {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

export default function BodyMapStep({
  value, onChange, onNext, onPhaseChange: _onPhaseChange, startAtPin, heading,
  sizeQuestion, pinPromptOverlay, toastNoPinCopy, serviceType,
}: Props) {

  const isPiercing = serviceType === "piercing";

  /* ── Determine which zone list and size list to use ─────────────── */
  /* These are always fixed based on serviceType; they never bleed into
     the tattoo flow. For tattoos, ZONES and SIZE_OPTIONS are used.      */
  function getZoneList(view: BodyMapValue["view"]): Zone[] {
    if (!isPiercing) return ZONES;
    if (isHeadView(view)) return PIERCING_HEAD_ZONES;
    return PIERCING_BODY_ZONES;
  }

  function getSizeOptions(): typeof SIZE_OPTIONS | typeof PIERCING_TYPES {
    if (!isPiercing) return SIZE_OPTIONS;
    return PIERCING_TYPES;
  }

  /* Derive initial default view for piercing vs tattoo */
  const defaultView: BodyMapValue["view"] = isPiercing ? "head-front" : "front";

  const currentZoneList = getZoneList(value?.view ?? defaultView);
  const currentSizeOptions = getSizeOptions();

  const { zoneId: initZone, subId: initSub, sizeId: initSize, otherDesc: initOtherDesc } =
    deriveIdsFromValue(value?.placementNotes, value?.sizeInches, currentZoneList, currentSizeOptions);

  const [gender,     setGender]     = useState<"male" | "female">(value?.gender ?? "male");
  const [view,       setView]       = useState<BodyMapValue["view"]>(value?.view ?? defaultView);
  const [pos,        setPos]        = useState<{ x: number; y: number } | null>(
    value ? { x: value.x * VW, y: value.y * VH } : null
  );
  const [radius,     setRadius]     = useState(value ? value.radius * VH : DEFAULT_R);
  const [zone,       setZone]       = useState<string | null>(initZone);
  const [subLoc,     setSubLoc]     = useState<string | null>(initSub);
  const [sizeChoice, setSizeChoice] = useState<string | null>(initSize);
  const [otherDesc,  setOtherDesc]  = useState<string>(initOtherDesc);
  const otherDescRef = useRef(otherDesc);
  otherDescRef.current = otherDesc;
  const [imgLoaded,  setImgLoaded]  = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [rawTouch,   setRawTouch]   = useState<{ x: number; y: number } | null>(null);
  const [hoverPos,   setHoverPos]   = useState<{ x: number; y: number } | null>(null);
  const [wiggle,     setWiggle]     = useState(false);
  const [showToast,  setShowToast]  = useState(false);
  const [subStep,    setSubStep]    = useState<"pin" | "refine">(
    isPiercing || (!startAtPin && value && value.x != null) ? "refine" : "pin"
  );

  const svgRef          = useRef<SVGSVGElement>(null);
  const imgRef          = useRef<HTMLImageElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const pinchRef        = useRef<{ dist: number; r: number } | null>(null);
  const isDragging      = useRef(false);
  const toastTimer      = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const posRef          = useRef(pos);
  posRef.current        = pos;

  const pathCache = useRef<Map<string, Path2D>>(new Map());
  const [onSilhouette, setOnSilhouette] = useState(true);

  function buildBodyPath(v: BodyView): Path2D {
    const p = new Path2D();
    if (v === "front" || v === "back") {
      p.ellipse(45, 14, 14, 15, 0, 0, Math.PI * 2);
      p.rect(8, 27, 74, 75);
      p.rect(22, 100, 21, 59);
      p.rect(47, 100, 21, 59);
    } else {
      p.ellipse(46, 14, 13, 15, 0, 0, Math.PI * 2);
      p.rect(24, 27, 42, 75);
      p.rect(28, 100, 30, 59);
    }
    return p;
  }

  function buildHeadPath(v: HeadView): Path2D {
    const p = new Path2D();
    if (v === "head-front") {
      // Head oval + ears
      p.ellipse(45, 42, 25, 30, 0, 0, Math.PI * 2);
      // Left ear
      p.ellipse(17, 45, 7, 12, 0, 0, Math.PI * 2);
      // Right ear
      p.ellipse(73, 45, 7, 12, 0, 0, Math.PI * 2);
      // Neck
      p.rect(38, 80, 14, 14);
    } else {
      // Side view: head profile + ear region
      // Approximate profile bounding ellipse
      p.ellipse(45, 40, 22, 32, 0, 0, Math.PI * 2);
      // Ear (always on right side of canvas due to mirroring)
      p.ellipse(70, 50, 12, 20, 0, 0, Math.PI * 2);
      // Neck
      p.rect(34, 80, 16, 14);
    }
    return p;
  }

  const hitCanvas = useRef<HTMLCanvasElement | null>(null);
  function getHitCtx(): CanvasRenderingContext2D | null {
    if (!hitCanvas.current) {
      hitCanvas.current = document.createElement("canvas");
      hitCanvas.current.width  = VW;
      hitCanvas.current.height = VH;
    }
    return hitCanvas.current.getContext("2d");
  }

  function pointOnSilhouette(p: { x: number; y: number }, v: BodyMapValue["view"]): boolean {
    const cacheKey = v;
    let path = pathCache.current.get(cacheKey);
    if (!path) {
      if (isHeadView(v)) {
        path = buildHeadPath(v);
      } else {
        path = buildBodyPath(v);
      }
      pathCache.current.set(cacheKey, path);
    }
    const ctx = getHitCtx();
    if (!ctx) return true;
    return ctx.isPointInPath(path, p.x, p.y);
  }

  const _prevPhaseRef    = useRef<string>("none");
  const zoneRef       = useRef(zone);
  const subLocRef     = useRef(subLoc);
  const sizeChoiceRef = useRef(sizeChoice);
  zoneRef.current       = zone;
  subLocRef.current     = subLoc;
  sizeChoiceRef.current = sizeChoice;

  const toSVGCoords = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(VW, (clientX - rect.left) / rect.width  * VW)),
      y: Math.max(0, Math.min(VH, (clientY - rect.top)  / rect.height * VH)),
    };
  }, []);

  function buildPayload(
    p: { x: number; y: number }, r: number, v: BodyMapValue["view"], g: "male" | "female",
    zn: string | null, sl: string | null, sc: string | null,
    customDesc?: string
  ): BodyMapValue {
    const activeZoneList = getZoneList(v);
    const activeSizeOpts = getSizeOptions();
    const zoneObj = activeZoneList.find(z => z.id === zn);
    const subObj  = zoneObj?.subs.find(s => s.id === sl);
    const sizeObj = activeSizeOpts.find(o => o.id === sc);
    const desc = customDesc ?? otherDescRef.current;
    const pn = zoneObj
      ? zn === OTHER_ZONE_ID
        ? `${OTHER_ZONE_LABEL} — ${desc}`
        : subObj ? `${zoneObj.label} — ${subObj.label}` : undefined
      : undefined;
    const si = sizeObj ? `${sizeObj.label}${sizeObj.sub ? ` (${sizeObj.sub})` : ""}` : undefined;
    return { x: p.x / VW, y: p.y / VH, view: v, radius: r / VH, gender: g, sizeInches: si, placementNotes: pn };
  }

  function emitFull(
    p: { x: number; y: number }, r: number, v: BodyMapValue["view"], g: "male" | "female",
    zn: string | null, sl: string | null, sc: string | null
  ) {
    onChange(buildPayload(p, r, v, g, zn, sl, sc));
  }

  const emit = useCallback(
    (p: { x: number; y: number }, r: number, v: BodyMapValue["view"], g: "male" | "female") => {
      onChange(buildPayload(p, r, v, g, zoneRef.current, subLocRef.current, sizeChoiceRef.current));
    },
    [onChange]
  );

  function handlePointerDown(e: React.PointerEvent<SVGSVGElement>) {
    if (e.pointerType === "touch") return;
    e.preventDefault();
    isDragging.current = true;
    const coords = toSVGCoords(e.clientX, e.clientY);
    setPos(coords);
    emit(coords, radius, view, gender);
    (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId);
  }

  function handlePointerEnter(e: React.PointerEvent<SVGSVGElement>) {
    if (e.pointerType === "touch") return;
    setHoverPos(toSVGCoords(e.clientX, e.clientY));
  }

  function handlePointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (e.pointerType === "touch") return;
    const coords = toSVGCoords(e.clientX, e.clientY);
    setHoverPos(coords);
    if (!isDragging.current) return;
    setPos(coords);
    emit(coords, radius, view, gender);
  }

  function handlePointerLeave() {
    setHoverPos(null);
  }

  function handlePointerUp(e: React.PointerEvent<SVGSVGElement>) {
    if (e.pointerType === "touch") return;
    isDragging.current = false;
  }

  function handleWheel(e: React.WheelEvent<SVGSVGElement>) {
    e.preventDefault();
    setRadius(r => {
      const newR = Math.max(MIN_R, Math.min(MAX_R, r - e.deltaY * 0.015));
      if (posRef.current) emit(posRef.current, newR, view, gender);
      return newR;
    });
  }

  function applyOffset(raw: { x: number; y: number }) {
    const absOff = Math.abs(TOUCH_OFFSET_Y);
    const usable = Math.min(absOff, Math.max(0, VH - raw.y));
    return { x: raw.x, y: Math.max(0, raw.y - usable) };
  }

  function handleTouchStart(e: React.TouchEvent<SVGSVGElement>) {
    if (e.touches.length === 2) {
      pinchRef.current = { dist: getTouchDist(e.touches), r: radius };
      e.preventDefault();
    } else if (e.touches.length === 1) {
      e.preventDefault();
      const t   = e.touches[0];
      const raw = toSVGCoords(t.clientX, t.clientY);
      const off = applyOffset(raw);
      setRawTouch(raw);
      setIsTouching(true);
      setPos(off);
      emit(off, radius, view, gender);
    }
  }

  function handleTouchMove(e: React.TouchEvent<SVGSVGElement>) {
    e.preventDefault();
    if (e.touches.length === 2 && pinchRef.current) {
      const dist  = getTouchDist(e.touches);
      const scale = dist / pinchRef.current.dist;
      const newR  = Math.max(MIN_R, Math.min(MAX_R, pinchRef.current.r * scale));
      setRadius(newR);
      if (posRef.current) emit(posRef.current, newR, view, gender);
    } else if (e.touches.length === 1) {
      const t   = e.touches[0];
      const raw = toSVGCoords(t.clientX, t.clientY);
      const off = applyOffset(raw);
      setRawTouch(raw);
      setPos(off);
      emit(off, radius, view, gender);
    }
  }

  function handleTouchEnd() {
    pinchRef.current = null;
    setIsTouching(false);
    setRawTouch(null);
  }

  function switchView(v: BodyMapValue["view"]) {
    if (v === view) return;
    setView(v);
    setPos(null);
    setRadius(DEFAULT_R);
    setImgLoaded(false);
    // When switching between head and body views in piercing mode,
    // reset zone/sub/size since the zone lists differ.
    if (isPiercing) {
      const wasHead = isHeadView(view);
      const willHead = isHeadView(v);
      if (wasHead !== willHead) {
        setZone(null);
        setSubLoc(null);
        setSizeChoice(null);
      }
    }
  }

  function switchGender(g: "male" | "female") {
    if (g === gender) return;
    setGender(g);
    setImgLoaded(false);
  }

  const canPinNext   = !!pos && onSilhouette;
  const refinePhase  = !zone ? "zone" : !subLoc ? "sub" : !sizeChoice ? "size" : "done";
  const canFinish    = !!zone && !!subLoc && !!sizeChoice && (zone !== OTHER_ZONE_ID || !!otherDesc.trim());

  function handlePinConfirm() {
    if (canPinNext) { setSubStep("refine"); return; }
    setWiggle(true);
    setShowToast(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setShowToast(false), 3500);
  }

  function handleFinish() {
    if (canFinish) onNext();
  }

  const loupeCX = rawTouch
    ? Math.max(LOUPE_R + 1, Math.min(VW - LOUPE_R - 1, rawTouch.x))
    : 0;
  const loupeCY = rawTouch
    ? Math.max(LOUPE_R + 1, rawTouch.y - LOUPE_LIFT)
    : 0;
  const loupeTransform = (rawTouch && pos)
    ? `translate(${loupeCX},${loupeCY}) scale(${LOUPE_ZOOM}) translate(${-pos.x},${-pos.y})`
    : "";

  const radiusPct  = Math.round(((radius - MIN_R) / (MAX_R - MIN_R)) * 100);

  /* For body views, use the img tag; for head views, use inline SVG */
  const isCurrentHeadView = isHeadView(view);
  const currentBodySrc = (!isCurrentHeadView && isBodyView(view))
    ? BODY_IMAGE[gender][view]
    : null;

  useEffect(() => {
    if (!pos) { setOnSilhouette(true); return; }
    const handle = requestAnimationFrame(() => {
      setOnSilhouette(pointOnSilhouette(pos, view));
    });
    return () => cancelAnimationFrame(handle);
  }, [pos, view]);

  useEffect(() => {
    if (!currentBodySrc) return;
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) setImgLoaded(true);
  }, [currentBodySrc]);

  // Head views are always "loaded" — no img to wait for
  const isMapReady = isCurrentHeadView ? true : imgLoaded;

  useEffect(() => {
    if (subStep !== "pin") return;
    const el = mapContainerRef.current;
    if (!el) return;
    const stop = (e: WheelEvent) => e.preventDefault();
    el.addEventListener("wheel", stop, { passive: false });
    return () => el.removeEventListener("wheel", stop);
  }, [subStep]);

  /* ── Derived zone/sub/size for the refine screen ─────────────────── */
  const activeZoneList = getZoneList(view);
  const activeSizeOpts = getSizeOptions();
  const activeZone = activeZoneList.find(z => z.id === zone);
  const activeSub  = activeZone?.subs.find(s => s.id === subLoc);
  const activeSize = activeSizeOpts.find(o => o.id === sizeChoice);

  /* ── Shared thumbnail image/svg for the refine screen ─────────────── */
  const thumbnailContent = isCurrentHeadView
    ? null
    : currentBodySrc;

  const cardBase = "group w-full flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-white/10 bg-white/[0.04] hover:border-violet-400/60 hover:bg-violet-500/5 active:scale-[0.96] transition-all text-center min-h-[56px] cursor-pointer";
  const backChip  = "inline-flex items-center gap-1 text-[11px] font-semibold text-white/40 hover:text-white/70 transition-colors cursor-pointer mb-1";

  const sharedStyles = `
    @keyframes bm-wiggle {
      0%,100% { transform: translateX(0) rotate(0deg); }
      15%     { transform: translateX(-7px) rotate(-1.5deg); }
      35%     { transform: translateX(7px)  rotate(1.5deg);  }
      50%     { transform: translateX(-5px) rotate(-0.8deg); }
      65%     { transform: translateX(5px)  rotate(0.8deg);  }
      82%     { transform: translateX(-2px); }
    }
    .bm-wiggle { animation: bm-wiggle 0.55s ease-out both; }

    @keyframes bm-toast-in {
      from { opacity:0; transform: translateY(8px) scale(0.93); }
      to   { opacity:1; transform: translateY(0)   scale(1);    }
    }
    .bm-toast { animation: bm-toast-in 0.22s ease-out forwards; }

    @keyframes bm-pop {
      0%   { opacity:0; transform: scale(0.2); }
      60%  { opacity:1; transform: scale(1.18); }
      80%  { transform: scale(0.95); }
      100% { transform: scale(1); }
    }
    .bm-marker-pop { animation: bm-pop 0.32s cubic-bezier(0.34,1.56,0.64,1) both; }

    @keyframes bm-cards-in {
      from { opacity:0; transform: translateY(10px); }
      to   { opacity:1; transform: translateY(0); }
    }
    .bm-cards-in { animation: bm-cards-in 0.22s ease-out both; }

    @keyframes bm-prompt-pulse {
      0%,100% { opacity:1; transform: scale(1);    }
      50%     { opacity:0.7; transform: scale(0.96); }
    }
    .bm-prompt-pulse { animation: bm-prompt-pulse 2.2s ease-in-out infinite; }

    @keyframes bm-prompt-in {
      from { opacity:0; transform: scale(0.92); }
      to   { opacity:1; transform: scale(1); }
    }
    .bm-prompt-in { animation: bm-prompt-in 0.25s ease-out both; }
  `;

  // ── SCREEN 1: Pin placement ──────────────────────────────────────────────
  if (subStep === "pin") {
    return (
      <div className="space-y-5 select-none">
        <style>{sharedStyles}</style>

        <div className="text-center space-y-2">
          <h3 className="font-heading text-xl text-white tracking-wide" data-testid="text-body-map-heading">{heading ?? "Where does the tattoo go?"}</h3>
          <p className="text-xs font-semibold tracking-wide" style={{ color: "rgba(167,139,250,0.85)" }}>
            👌 Pinch with two fingers to make the circle bigger or smaller
          </p>
          <p className="text-xs text-white/60 leading-relaxed max-w-xs mx-auto pt-1">
            Tap the body to drop a pin, then drag it to the exact spot.
          </p>
        </div>

        {/* gender toggle */}
        <div className="flex items-center justify-center">
          <div
            className="flex rounded-full p-0.5 gap-0.5"
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
          >
            {(["male", "female"] as const).map(g => (
              <button
                key={g}
                onClick={() => switchGender(g)}
                data-testid={`button-body-gender-${g}`}
                className="px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all min-h-[44px]"
                style={{
                  background: gender === g ? "rgba(139,92,246,0.85)" : "transparent",
                  color:      gender === g ? "#fff"                    : "rgba(255,255,255,0.35)",
                  boxShadow:  gender === g ? "0 2px 10px rgba(139,92,246,0.35)" : "none",
                }}
              >
                {g === "male" ? "♂ Male" : "♀ Female"}
              </button>
            ))}
          </div>
        </div>

        {/* view selector — tattoo: single row; piercing: two labeled groups */}
        {isPiercing ? (
          <div className="flex flex-col items-center gap-2">
            {/* Head group */}
            <div className="flex flex-col items-center gap-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">Head</span>
              <div
                className="flex rounded-full p-0.5 gap-0.5"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                {PIERCING_HEAD_VIEW_OPTIONS.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => switchView(id)}
                    data-testid={`button-body-view-${id}`}
                    className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all min-h-[44px]"
                    style={{
                      background: view === id ? "rgba(139,92,246,0.85)" : "transparent",
                      color:      view === id ? "#fff"                   : "rgba(255,255,255,0.35)",
                      boxShadow:  view === id ? "0 2px 10px rgba(139,92,246,0.35)" : "none",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {/* Body group */}
            <div className="flex flex-col items-center gap-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">Body</span>
              <div
                className="flex rounded-full p-0.5 gap-0.5"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
              >
                {PIERCING_BODY_VIEW_OPTIONS.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => switchView(id)}
                    data-testid={`button-body-view-${id}`}
                    className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all min-h-[44px]"
                    style={{
                      background: view === id ? "rgba(139,92,246,0.85)" : "transparent",
                      color:      view === id ? "#fff"                   : "rgba(255,255,255,0.35)",
                      boxShadow:  view === id ? "0 2px 10px rgba(139,92,246,0.35)" : "none",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Tattoo view selector — byte-for-byte identical to original */
          <div className="flex items-center justify-center">
            <div
              className="flex rounded-full p-0.5 gap-0.5"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              {VIEW_OPTIONS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => switchView(id)}
                  data-testid={`button-body-view-${id}`}
                  className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all min-h-[44px]"
                  style={{
                    background: view === id ? "rgba(139,92,246,0.85)" : "transparent",
                    color:      view === id ? "#fff"                   : "rgba(255,255,255,0.35)",
                    boxShadow:  view === id ? "0 2px 10px rgba(139,92,246,0.35)" : "none",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* body image + interactive SVG */}
        <div className="flex flex-col items-center gap-4">
          <div
            ref={mapContainerRef}
            className="relative rounded-xl overflow-hidden"
            style={{ width: "min(200px, 50vw)", aspectRatio: "9 / 16" }}
          >
            {/* Background shimmer — shown while image loads (body views only) */}
            {!isCurrentHeadView && (
              <div
                className="absolute inset-0 rounded-xl"
                style={{
                  background: "linear-gradient(135deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))",
                  opacity:    imgLoaded ? 0 : 1,
                  transition: "opacity 0.3s ease",
                }}
              />
            )}

            {/* Dark background for head views */}
            {isCurrentHeadView && (
              <div
                className="absolute inset-0 rounded-xl"
                style={{ background: "linear-gradient(160deg,rgba(20,14,40,0.95),rgba(10,8,25,0.98))" }}
              />
            )}

            {/* Body silhouette image (body views only) */}
            {currentBodySrc && (
              <img
                key={currentBodySrc}
                ref={imgRef}
                src={currentBodySrc}
                alt={`${gender} body ${view} view`}
                className="absolute inset-0 w-full h-full select-none pointer-events-none"
                draggable={false}
                onLoad={() => setImgLoaded(true)}
                style={{ objectFit: "cover", opacity: imgLoaded ? 0.82 : 0, transition: "opacity 0.35s ease" }}
              />
            )}

            {/* Head SVG silhouette (head views only — piercing only) */}
            {isCurrentHeadView && (
              <HeadSVG view={view as HeadView} gender={gender} />
            )}

            {/* Tap prompt overlay — shown until first pin placed */}
            {!pos && isMapReady && (
              <div
                className="bm-prompt-in absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
                style={{ background: "rgba(0,0,0,0.52)", backdropFilter: "blur(1.5px)", zIndex: 10 }}
              >
                <div
                  className="bm-prompt-pulse flex flex-col items-center gap-2 px-4 py-3 rounded-2xl text-center"
                  style={{ background: "rgba(139,92,246,0.18)", border: "1px solid rgba(139,92,246,0.5)", boxShadow: "0 0 20px rgba(139,92,246,0.2)" }}
                >
                  <span style={{ fontSize: "1.4rem", lineHeight: 1 }}>📍</span>
                  <p className="text-white font-bold leading-snug tracking-wide" style={{ fontSize: "0.62rem", textShadow: "0 1px 6px rgba(0,0,0,0.9)" }}>
                    {pinPromptOverlay
                      ? pinPromptOverlay.split("\n").map((line, i) => <span key={i}>{line}{i < pinPromptOverlay.split("\n").length - 1 ? <br /> : null}</span>)
                      : isPiercing
                        ? <>Tap or click to<br />place your piercing<br />location</>
                        : <>Tap or click to<br />place your tattoo<br />location</>}
                  </p>
                </div>
              </div>
            )}

            <svg
              ref={svgRef}
              viewBox={`0 0 ${VW} ${VH}`}
              className="absolute inset-0 w-full h-full touch-none cursor-crosshair select-none"
              style={{ zIndex: 3, userSelect: "none", WebkitUserSelect: "none" }}
              onPointerDown={handlePointerDown}
              onPointerEnter={handlePointerEnter}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={handlePointerLeave}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              data-testid="body-map-svg"
            >
              <defs>
                <radialGradient id="bm-spot" cx="50%" cy="50%" r="50%">
                  <stop offset="0%"   stopColor="#a78bfa" stopOpacity="0.92" />
                  <stop offset="40%"  stopColor="#8b5cf6" stopOpacity="0.65" />
                  <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.08" />
                </radialGradient>
                <radialGradient id="bm-ring" cx="50%" cy="50%" r="50%">
                  <stop offset="70%"  stopColor="#a78bfa" stopOpacity="0"    />
                  <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.85" />
                </radialGradient>
                <radialGradient id="bm-shadow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%"   stopColor="#000000" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#000000" stopOpacity="0"    />
                </radialGradient>
                {isTouching && rawTouch && (
                  <clipPath id="bm-loupe-clip">
                    <circle cx={loupeCX} cy={loupeCY} r={LOUPE_R} />
                  </clipPath>
                )}
              </defs>

              {/* Ghost hover preview */}
              {hoverPos && !pos && (
                <g style={{ pointerEvents: "none" }}>
                  <circle cx={hoverPos.x} cy={hoverPos.y} r={radius * 1.5} fill="rgba(139,92,246,0.08)" />
                  <circle cx={hoverPos.x} cy={hoverPos.y} r={radius} fill="rgba(167,139,250,0.28)" />
                  <circle cx={hoverPos.x} cy={hoverPos.y} r={radius} fill="none" stroke="#a78bfa" strokeWidth="0.7" strokeOpacity="0.75" strokeDasharray="2 1.5" />
                  <line x1={hoverPos.x - radius - 3} y1={hoverPos.y} x2={hoverPos.x + radius + 3} y2={hoverPos.y} stroke="#a78bfa" strokeWidth="0.45" strokeOpacity="0.55" />
                  <line x1={hoverPos.x} y1={hoverPos.y - radius - 3} x2={hoverPos.x} y2={hoverPos.y + radius + 3} stroke="#a78bfa" strokeWidth="0.45" strokeOpacity="0.55" />
                  <circle cx={hoverPos.x} cy={hoverPos.y} r={1.6} fill="rgba(196,181,253,0.9)" />
                  <circle cx={hoverPos.x} cy={hoverPos.y} r={0.7} fill="rgba(255,255,255,1)" />
                </g>
              )}

              {pos && (
                <g className="bm-marker-pop" style={{ transformOrigin: `${pos.x}px ${pos.y}px` }}>
                  <circle cx={pos.x} cy={pos.y} r={radius * 1.6} fill="url(#bm-shadow)" />
                  <circle cx={pos.x} cy={pos.y} r={radius} fill="url(#bm-spot)" />
                  <circle cx={pos.x} cy={pos.y} r={radius} fill="url(#bm-ring)" />
                  <circle cx={pos.x} cy={pos.y} r={radius} fill="none" stroke="#a78bfa" strokeWidth="0.4" strokeOpacity="0.7" strokeDasharray="1.5 1" />
                  <circle cx={pos.x} cy={pos.y} r={1.4} fill="#c4b5fd" />
                  <circle cx={pos.x} cy={pos.y} r={0.7} fill="#fff"    />
                </g>
              )}

              <text x={VW / 2} y={VH - 3} textAnchor="middle" fontSize="4" fill="rgba(255,255,255,0.25)" fontFamily="system-ui,sans-serif" fontWeight="700" letterSpacing="2" style={{ textTransform: "uppercase", userSelect: "none" }}>
                {isCurrentHeadView
                  ? (view === "head-front" ? "face" : view === "head-left" ? "left ear" : "right ear")
                  : view}
              </text>

              {isTouching && rawTouch && pos && (
                <>
                  <g clipPath="url(#bm-loupe-clip)">
                    <circle cx={loupeCX} cy={loupeCY} r={LOUPE_R} fill="rgb(6,4,18)" />
                    {/* Loupe: show body image for body views, head outline for head views */}
                    {currentBodySrc && (
                      <image href={currentBodySrc} x={0} y={0} width={VW} height={VH} opacity={imgLoaded ? 0.88 : 0} transform={loupeTransform} />
                    )}
                    <g transform={loupeTransform}>
                      <circle cx={pos.x} cy={pos.y} r={radius} fill="url(#bm-spot)" />
                      <circle cx={pos.x} cy={pos.y} r={radius} fill="url(#bm-ring)" />
                      <circle cx={pos.x} cy={pos.y} r={radius} fill="none" stroke="#a78bfa" strokeWidth="0.5" strokeOpacity="0.9" strokeDasharray="1.5 1" />
                      <circle cx={pos.x} cy={pos.y} r={1.4} fill="#c4b5fd" />
                      <circle cx={pos.x} cy={pos.y} r={0.7} fill="#fff"    />
                    </g>
                    <line x1={loupeCX - LOUPE_R} y1={loupeCY} x2={loupeCX + LOUPE_R} y2={loupeCY} stroke="rgba(255,255,255,0.2)" strokeWidth="0.35" />
                    <line x1={loupeCX} y1={loupeCY - LOUPE_R} x2={loupeCX} y2={loupeCY + LOUPE_R} stroke="rgba(255,255,255,0.2)" strokeWidth="0.35" />
                  </g>
                  <circle cx={loupeCX} cy={loupeCY} r={LOUPE_R} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="0.75" />
                  <line x1={loupeCX} y1={loupeCY + LOUPE_R + 0.5} x2={rawTouch.x} y2={rawTouch.y - 2} stroke="rgba(255,255,255,0.25)" strokeWidth="0.45" strokeDasharray="1.8 0.9" />
                </>
              )}
            </svg>
          </div>

          {/* size slider */}
          <div className="w-full max-w-xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-white/70 uppercase tracking-widest font-semibold">Mark coverage area</span>
              <span className="text-[10px] text-white/60">
                {radiusPct < 20 ? "Small" : radiusPct < 45 ? "Medium" : radiusPct < 70 ? "Large" : "X-Large"}
              </span>
            </div>
            <input
              type="range" min={MIN_R} max={MAX_R} step={0.5} value={radius}
              data-testid="slider-body-map-radius"
              onChange={e => {
                const newR = Number(e.target.value);
                setRadius(newR);
                if (pos) emit(pos, newR, view, gender);
              }}
              className="w-full"
              style={{ accentColor: "#8b5cf6" }}
            />
            <div className="flex justify-between text-[10px] text-white/50">
              <span>Tiny</span>
              <span>X-Large</span>
            </div>
          </div>
        </div>

        {/* confirm button + toast */}
        <div className="flex flex-col items-center gap-3 pt-1">
          <button
            onClick={handlePinConfirm}
            data-testid="button-body-map-next"
            onAnimationEnd={() => setWiggle(false)}
            className={[
              "inline-flex items-center justify-center text-white uppercase tracking-widest text-xs font-bold font-subheading transition-all w-full sm:w-auto px-8 py-4 rounded-full",
              canPinNext ? "bg-accent hover:bg-accent/80" : "bg-accent/40 cursor-default",
              wiggle ? "bm-wiggle" : "",
            ].join(" ")}
          >
            {!pos ? "Tap to place a pin" : !onSilhouette ? "Move the pin onto the body outline" : "Next: Describe the spot →"}
          </button>

          {showToast && (
            <div
              className="bm-toast flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold"
              style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.4)", backdropFilter: "blur(10px)", color: "rgba(255,255,255,0.88)" }}
            >
              <span>{pos && !onSilhouette ? "⚠️" : "📍"}</span>
              <span>{pos && !onSilhouette ? "Move the pin onto the body outline to continue" : (toastNoPinCopy ?? "Tap the body map above to mark your tattoo location first")}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── SCREEN 2: Where Exactly? ─────────────────────────────────────────────
  return (
    <div className="space-y-5 select-none">
      <style>{sharedStyles}</style>

      {/* back button — hidden for piercing since pin placement is not part of that flow */}
      {!isPiercing && (
        <button
          data-testid="button-body-map-back-to-pin"
          onClick={() => setSubStep("pin")}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 bg-white/[0.04] text-white/70 font-subheading text-xs uppercase tracking-widest hover:bg-white/[0.08] hover:text-white active:scale-[0.97] transition-all min-h-[44px]"
        >
          <ArrowLeft size={12} /> Reposition pin
        </button>
      )}

      {/* centered placement thumbnail */}
      {pos && (
        <div className="flex justify-center">
          <div
            className="relative rounded-xl overflow-hidden"
            style={{ width: 72, aspectRatio: "9/16", border: "1px solid rgba(139,92,246,0.4)", boxShadow: "0 0 16px rgba(139,92,246,0.15)" }}
          >
            {isCurrentHeadView ? (
              <>
                <div className="absolute inset-0" style={{ background: "linear-gradient(160deg,rgba(20,14,40,0.95),rgba(10,8,25,0.98))" }} />
                <HeadSVG view={view as HeadView} gender={gender} />
              </>
            ) : (
              thumbnailContent && (
                <img
                  src={thumbnailContent}
                  alt={`${gender} ${view}`}
                  className="absolute inset-0 w-full h-full pointer-events-none select-none"
                  draggable={false}
                  style={{ objectFit: "cover", opacity: 0.8 }}
                />
              )
            )}
            <svg viewBox={`0 0 ${VW} ${VH}`} className="absolute inset-0 w-full h-full pointer-events-none">
              <circle cx={pos.x} cy={pos.y} r={radius} fill="rgba(139,92,246,0.7)" />
              <circle cx={pos.x} cy={pos.y} r={1.8} fill="#fff" />
            </svg>
          </div>
        </div>
      )}

      <div className="text-center space-y-1">
        <h3 className="font-heading text-xl text-white tracking-wide">Where Exactly?</h3>
        <p className="text-xs text-white/60">Tell your artist the precise location.</p>
      </div>

      {/* zone picker */}
      {refinePhase === "zone" && (
        <div className="bm-cards-in space-y-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 text-center">
            {isPiercing ? "Which piercing area?" : "Which body area?"}
          </p>
          {/* responsive-audit-allow: intentional always-2-col on mobile (commit 322a2f38) */}
          <div className="grid grid-cols-2 gap-2">
            {activeZoneList.map(z => (
              <button
                key={z.id}
                data-testid={`button-zone-${z.id}`}
                onClick={() => {
                  setZone(z.id);
                  setSubLoc(null);
                  setSizeChoice(null);
                  emitFull(posRef.current ?? { x: VW / 2, y: VH / 2 }, radius, view, gender, z.id, null, null);
                }}
                className={z.id === OTHER_ZONE_ID ? `${cardBase} col-span-2 flex-row gap-2.5 h-11` : cardBase}
              >
                <span className="text-2xl leading-none">{z.icon}</span>
                <span className="text-xs font-semibold text-white group-hover:text-violet-300 transition-colors leading-tight">{z.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* sub-location picker */}
      {refinePhase === "sub" && activeZone && (
        <div className="bm-cards-in space-y-3">
          <div className="flex items-center gap-2">
            <button
              data-testid="button-body-back-to-zone"
              onClick={() => { setZone(null); setSubLoc(null); setSizeChoice(null); }}
              className={backChip}
            >
              <span>←</span>
              <span>{activeZone.icon} {activeZone.label}</span>
            </button>
          </div>

          {activeZone.id === OTHER_ZONE_ID ? (
            /* "Somewhere Else" — freetext description input */
            <div className="space-y-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">Describe the placement</p>
              <textarea
                data-testid="input-other-placement"
                rows={3}
                maxLength={200}
                placeholder="e.g. Behind my left ear, along the jawline…"
                value={otherDesc}
                onChange={e => {
                  const v = e.target.value;
                  setOtherDesc(v);
                  emitFull(posRef.current ?? { x: VW / 2, y: VH / 2 }, radius, view, gender, zone, subLoc, sizeChoice);
                }}
                className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder:text-white/50 resize-none focus:outline-none focus:border-violet-400/60 focus:bg-violet-500/5 transition-all"
              />
              <button
                data-testid="button-other-placement-continue"
                disabled={!otherDesc.trim()}
                onClick={() => {
                  const desc = otherDesc.trim();
                  if (!desc) return;
                  setSubLoc(OTHER_SUB_ID);
                  setSizeChoice(null);
                  onChange(buildPayload(posRef.current ?? { x: VW / 2, y: VH / 2 }, radius, view, gender, zone, OTHER_SUB_ID, null, desc));
                }}
                className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all${otherDesc.trim() ? " btn-cta-glow" : ""}`}
                style={{
                  background: otherDesc.trim() ? "rgba(139,92,246,0.15)" : "rgba(255,255,255,0.04)",
                  border: `1px solid ${otherDesc.trim() ? "rgba(139,92,246,0.5)" : "rgba(255,255,255,0.08)"}`,
                  color: otherDesc.trim() ? "#c4b5fd" : "#555",
                  cursor: otherDesc.trim() ? "pointer" : "not-allowed",
                }}
              >
                Continue →
              </button>
            </div>
          ) : (
            /* Normal sub-location grid */
            <>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">Which part?</p>
              {/* responsive-audit-allow: intentional always-2-col on mobile (commit 322a2f38) */}
              <div className="grid grid-cols-2 gap-2">
                {activeZone.subs.map((s, i) => (
                  <button
                    key={s.id}
                    data-testid={`button-subloc-${s.id}`}
                    onClick={() => {
                      setSubLoc(s.id);
                      setSizeChoice(null);
                      emitFull(posRef.current ?? { x: VW / 2, y: VH / 2 }, radius, view, gender, zone, s.id, null);
                    }}
                    className={`${cardBase}${activeZone.subs.length % 2 !== 0 && i === activeZone.subs.length - 1 ? " col-span-2" : ""}`}
                  >
                    <span className="text-xs font-semibold text-white group-hover:text-violet-300 transition-colors leading-tight">{s.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* size / piercing-type picker */}
      {refinePhase === "size" && activeSub && (
        <div className="bm-cards-in space-y-3">
          <div className="flex items-center gap-2">
            <button
              data-testid="button-body-back-to-sub"
              onClick={() => { setSubLoc(null); setSizeChoice(null); }}
              className={backChip}
            >
              <span>←</span>
              <span>{activeSub.label}</span>
            </button>
          </div>
          {isPiercing ? (
            <>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/70" data-testid="text-body-map-size-question">
                Jewellery type
              </p>
              <p className="text-[10px] text-white/50">Your artist will advise on final jewellery choice.</p>
              {/* responsive-audit-allow: intentional always-2-col on mobile (commit 322a2f38) */}
              <div className="grid grid-cols-2 gap-2">
                {PIERCING_TYPES.filter(pt => pt.id !== "artist-choice").map((pt, i, arr) => (
                  <button
                    key={pt.id}
                    data-testid={`button-size-${pt.id}`}
                    onClick={() => {
                      setSizeChoice(pt.id);
                      emitFull(posRef.current ?? { x: VW / 2, y: VH / 2 }, radius, view, gender, zone, subLoc, pt.id);
                    }}
                    className={`${cardBase}${arr.length % 2 !== 0 && i === arr.length - 1 ? " col-span-2" : ""}`}
                  >
                    <span className="text-sm font-bold text-white group-hover:text-violet-300 transition-colors">{pt.label}</span>
                    <span className="text-[10px] text-white/60">{pt.sub}</span>
                  </button>
                ))}
                <button
                  data-testid="button-size-artist-choice"
                  onClick={() => {
                    setSizeChoice("artist-choice");
                    emitFull(posRef.current ?? { x: VW / 2, y: VH / 2 }, radius, view, gender, zone, subLoc, "artist-choice");
                  }}
                  className="col-span-2 w-full group flex flex-col items-center justify-center gap-0.5 rounded-xl border-2 border-dashed border-white/20 px-3 py-3 text-center transition-all hover:border-violet-400/50 hover:bg-violet-500/5 min-h-[56px]"
                >
                  <span className="text-sm font-bold text-white/60 group-hover:text-violet-300 transition-colors">I'm not sure — let my artist decide</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/70" data-testid="text-body-map-size-question">{sizeQuestion ?? "How big is the tattoo?"}</p>
              {/* responsive-audit-allow: intentional always-2-col on mobile (commit 322a2f38) */}
              <div className="grid grid-cols-2 gap-2">
                {SIZE_OPTIONS.map((sz, i) => (
                  <button
                    key={sz.id}
                    data-testid={`button-size-${sz.id}`}
                    onClick={() => {
                      setSizeChoice(sz.id);
                      emitFull(posRef.current ?? { x: VW / 2, y: VH / 2 }, radius, view, gender, zone, subLoc, sz.id);
                    }}
                    className={`${cardBase}${SIZE_OPTIONS.length % 2 !== 0 && i === SIZE_OPTIONS.length - 1 ? " col-span-2" : ""}`}
                  >
                    <span className="text-sm font-bold text-white group-hover:text-violet-300 transition-colors">{sz.label}</span>
                    <span className="text-[10px] text-white/60">{sz.sub}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* done summary chips */}
      {refinePhase === "done" && activeZone && activeSub && activeSize && (
        <div className="bm-cards-in space-y-2">
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="button-body-edit-location"
              onClick={() => { setZone(null); setSubLoc(null); setSizeChoice(null); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-semibold transition-all min-h-[44px]"
              style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.4)", color: "#c4b5fd" }}
            >
              <span>📍</span>
              <span>
                {activeZone.id === OTHER_ZONE_ID
                  ? `Somewhere Else — ${otherDesc || "Custom"}`
                  : `${activeZone.label} — ${activeSub.label}`}
              </span>
              <span className="text-white/50 ml-0.5">✎</span>
            </button>
            <button
              data-testid="button-body-edit-size"
              onClick={() => setSizeChoice(null)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-semibold transition-all min-h-[44px]"
              style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.4)", color: "#c4b5fd" }}
            >
              <span>{isPiercing ? "💎" : "📐"}</span>
              <span>{activeSize.label}{!isPiercing ? ` (${(activeSize as typeof SIZE_OPTIONS[0]).sub})` : ""}</span>
              <span className="text-white/50 ml-0.5">✎</span>
            </button>
          </div>
        </div>
      )}

      {/* finish button */}
      <div className="flex flex-col items-center gap-3 pt-1">
        <button
          onClick={handleFinish}
          data-testid="button-body-map-finish"
          className={[
            "inline-flex items-center justify-center text-white uppercase tracking-widest text-xs font-bold font-subheading transition-all w-full sm:w-auto px-8 py-4 rounded-full",
            canFinish ? "bg-accent hover:bg-accent/80" : "bg-accent/40 cursor-default",
          ].join(" ")}
        >
          {!zone ? "Select a body area above" : !subLoc ? "Select the exact location above" : !sizeChoice ? (isPiercing ? "Select jewellery type above" : "Select a size above") : "Looks Good →"}
        </button>
      </div>
    </div>
  );
}
